import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import ParticipantsList from "./participantsList";
import Participant from "./participant";

export interface EnabledCountry {
  code: string;
  enabled: boolean;
}

export interface CountryStats {
  code: string;
  total: number;
  odds: number;
  poolShare: number;
}

export interface ActivityItem {
  betId: string;
  bettor: string;
  countryCode: string;
  amount: number;
  timestamp: number;
}

export interface DappState {
  isLoaded?: boolean;
  dapp?: any;
  participants: ParticipantsList;
  participantsTotal: number;
  participantsHasMore: boolean;
  participantsPage: number;
  balance: number;
  winner?: string;
  ratio?: number;
  isActive: boolean;
  enabledCountries: EnabledCountry[];
  countryStats: CountryStats[];
  recentActivity: ActivityItem[];
}

const initialState: DappState = {
  isLoaded: false,
  dapp: undefined,
  participants: new ParticipantsList(),
  participantsTotal: 0,
  participantsHasMore: false,
  participantsPage: 0,
  balance: 0,
  isActive: true,
  enabledCountries: [],
  countryStats: [],
  recentActivity: [],
};

export const Slice = createSlice({
  name: "dapp",
  initialState,
  reducers: {
    setDapp: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        dapp: action.payload,
        balance: action.payload.balance || 0,
      };
    },
    setParticipants: (state, action: PayloadAction<any>) => {
      const { participants, total, hasMore, page, append } = action.payload;

      // If appending (load more), merge with existing
      if (append && state.participants) {
        const merged = new ParticipantsList(state.participants.getList());
        participants.forEach((p: Participant) => merged.set(p));
        return {
          ...state,
          participants: merged,
          participantsTotal: total ?? state.participantsTotal,
          participantsHasMore: hasMore ?? false,
          participantsPage: page ?? state.participantsPage,
          isLoaded: true,
        };
      }

      return {
        ...state,
        participants: participants,
        participantsTotal: total ?? 0,
        participantsHasMore: hasMore ?? false,
        participantsPage: page ?? 0,
        isLoaded: true,
      };
    },
    setParticipant: (state, action: PayloadAction<Participant>) => {
      let participants = new ParticipantsList(state.participants.getList());
      if (state && state.participants) {
        state.participants.set(action.payload);
        participants = new ParticipantsList(state.participants.getList());
      }
      return { ...state, participants };
    },
    setBalance: (state, action: PayloadAction<any>) => {
      return {
        ...state,
        balance: action.payload,
      };
    },
    setWinnerBet: (state, action: PayloadAction<any>) => {
      const { winner, ratio } = action.payload || {};
      return {
        ...state,
        winner,
        ratio,
      };
    },
    setIsActive: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isActive: action.payload,
      };
    },
    setEnabledCountries: (state, action: PayloadAction<EnabledCountry[]>) => {
      return {
        ...state,
        enabledCountries: action.payload,
      };
    },
    setCountryStats: (state, action: PayloadAction<CountryStats[]>) => {
      return {
        ...state,
        countryStats: action.payload,
      };
    },
    setRecentActivity: (state, action: PayloadAction<ActivityItem[]>) => {
      return {
        ...state,
        recentActivity: action.payload,
      };
    },
    removeParticipant: (state, action: PayloadAction<string>) => {
      let participants = new ParticipantsList(state.participants.getList());
      if (state && state.participants) {
        state.participants.delete(action.payload);
        participants = new ParticipantsList(state.participants.getList());
      }
      return { ...state, participants };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setDapp,
  setParticipants,
  setParticipant,
  removeParticipant,
  setBalance,
  setWinnerBet,
  setIsActive,
  setEnabledCountries,
  setCountryStats,
  setRecentActivity,
} = Slice.actions;

export const dapp = (state: RootState) => state.dapp.dapp;

export const balance = (state: RootState) => state.dapp.balance;
export const isActive = (state: RootState) => state.dapp.isActive;

export const winner = (state: RootState) => ({
  winner: state.dapp.winner,
  ratio: state.dapp.ratio,
});

export const participants = (state: RootState) =>
  state.dapp.participants || new ParticipantsList();

export const participantsTotal = (state: RootState) => state.dapp.participantsTotal;
export const participantsHasMore = (state: RootState) => state.dapp.participantsHasMore;
export const participantsPage = (state: RootState) => state.dapp.participantsPage;

export const dAppIsLoaded = (state: RootState) => state.dapp.isLoaded;

export const enabledCountries = (state: RootState) => state.dapp.enabledCountries;

export const countryStats = (state: RootState) => state.dapp.countryStats;
export const recentActivity = (state: RootState) => state.dapp.recentActivity;

export default Slice.reducer;
